import { Box, Modal } from "@mui/material";
import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  CustomButton,
  CustomDropdown,
  CustomLoader,
  FormModalTitle,
} from "../../../components";
import SigninTextInput from "../../../components/SigninTextInput";
import i18n from "../../../i18n-config";
import {
  createAsyncAdminUtilisateur,
  updateAsyncAdminUtilisateur,
  Utilisateur,
} from "../../../store/features/utilisateurSlice";
import theme from "../../../theme/theme";
import { ROLES } from "../../../utils/constants";
import notifyMessage from "../../../utils/notify";
import { capitalizeFirstLetter } from "../../../utils/strings";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: 400, md: 550, lg: 550 },
  backgroundColor: "#ffffff",
  borderRadius: 2,
  padding: 3,
};

interface Props {
  open: boolean;
  item?: Utilisateur;
  onClose(): void;
}

function UserForm({ open, item, onClose }: Props) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Utilisateur>({
    nom: item?.nom || "",
    prenom: item?.prenom || "",
    email: item?.email || "",
    telephone: item?.telephone || "+225",
    role: item?.roleUtilisateur || "",
    pin: "0000",
  });

  const dispatch = useDispatch();

  const onChangeField = (key: string, value: string) => {
    setFormData({
      ...formData,
      [key]: value,
    });
  };

  const handleClose = () => {
    onClose();
  };

  const toggleLoader = () => {
    setLoading(!loading);
  };

  const callback = () => {
    setLoading(false);
    handleClose();
    notifyMessage({
      message: capitalizeFirstLetter(i18n.t("create_success")),
      severity: "success",
    });
  };

  const onError = () => {
    setLoading(false);
    notifyMessage({
      message: capitalizeFirstLetter(i18n.t("error_occured")),
      severity: "error",
    });
  };

  const onSubmit = () => {
    toggleLoader();
    if (!item) {
      dispatch(createAsyncAdminUtilisateur(formData, callback, onError) as any);
    } else {
      dispatch(
        updateAsyncAdminUtilisateur(
          { ...item, ...formData },
          callback,
          onError
        ) as any
      );
    }
  };

  const disabled =
    !formData.nom.trim() ||
    !formData.telephone.trim() ||
    !formData.email?.trim() ||
    !formData.role.trim();

  return (
    <Modal open={open} onClose={handleClose}>
      <form>
        <Box sx={style}>
          <FormModalTitle
            title={i18n.t(!item ? "creer_user" : "update_user")}
            onClose={handleClose}
          />
          <SigninTextInput
            title={capitalizeFirstLetter(i18n.t("nom"))}
            value={formData.nom}
            onChange={(value: string) => onChangeField("nom", value)}
          />
          <SigninTextInput
            title={capitalizeFirstLetter(i18n.t("prenom"))}
            value={formData.prenom}
            onChange={(value: string) => onChangeField("prenom", value)}
          />
          <SigninTextInput
            title={capitalizeFirstLetter(i18n.t("email"))}
            value={formData.email}
            onChange={(value: string) => onChangeField("email", value)}
          />
          <SigninTextInput
            title={capitalizeFirstLetter(i18n.t("telephone"))}
            value={formData.telephone}
            onChange={(value: string) => onChangeField("telephone", value)}
          />

          <CustomDropdown
            title={i18n.t("role")}
            value={formData.role}
            items={ROLES}
            handleChange={(value: string) => onChangeField("role", value)}
          />

          <CustomButton
            title={capitalizeFirstLetter(
              i18n.t(!item ? "ajouter" : "modifier")
            )}
            onClick={onSubmit}
            disabled={disabled}
            rootStyle={{
              width: "100%",
              height: 50,
              marginTop: 3,
              marginBottom: 2,
              backgroundColor: disabled
                ? theme.colors.disabled
                : theme.colors.primary,
            }}
            titleStyle={{ color: theme.colors.white }}
          />
        </Box>
        <CustomLoader open={loading} handleClose={toggleLoader} />
      </form>
    </Modal>
  );
}

export default UserForm;
